import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Building, CheckCircle2, Plus, ArrowRight, Loader2 } from 'lucide-react';
import { ReconciliationIssueRecord, SupplierRecord, BuyerRecord } from '@/types/database';
import { reconciliationService } from '@/services/reconciliationService';
import { supabase } from '@/lib/supabaseClient';

interface CounterpartyModalProps {
  issue: ReconciliationIssueRecord | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CounterpartyModal: React.FC<CounterpartyModalProps> = ({
  issue,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const isSupplier = issue?.entity_type === 'supplier' || issue?.issue_type.includes('supplier');
  const [decision, setDecision] = useState<'match_existing' | 'create_new' | 'reclassify' | 'unresolved'>('match_existing');
  const [suppliers, setSuppliers] = useState<SupplierRecord[]>([]);
  const [buyers, setBuyers] = useState<BuyerRecord[]>([]);
  const [matchedId, setMatchedId] = useState<string>('');
  const [newName, setNewName] = useState<string>('');
  const [newCategory, setNewCategory] = useState<string>('feed');
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!issue || !isOpen) return;

    setError(null);
    setNewName(issue.legacy_value || '');

    if (isSupplier) {
      supabase
        .from('suppliers')
        .select('*')
        .eq('is_void', false)
        .order('supplier_name', { ascending: true })
        .then(({ data }) => setSuppliers((data as any) || []));
    } else {
      supabase
        .from('buyers')
        .select('*')
        .eq('is_void', false)
        .order('buyer_name', { ascending: true })
        .then(({ data }) => setBuyers((data as any) || []));
    }
  }, [issue, isOpen, isSupplier]);

  const handleSubmit = async () => {
    if (!issue) return;
    setError(null);
    setSubmitting(true);

    try {
      const targetList = isSupplier ? suppliers : buyers;
      const matchedRecord: any = targetList.find((item: any) => item.id === matchedId);

      await reconciliationService.resolveCounterparty(
        issue.id,
        {
          decision,
          counterparty_type: isSupplier ? 'supplier' : 'buyer',
          matched_id: matchedId || undefined,
          matched_name: matchedRecord?.supplier_name || matchedRecord?.buyer_name,
          new_name: newName || undefined,
          new_category: newCategory || undefined,
          notes: adminNotes,
        },
        adminNotes
      );

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error resolving counterparty:', err);
      setError(err.message || 'Failed to save counterparty resolution');
    } finally {
      setSubmitting(false);
    }
  };

  if (!issue) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-amber-50 text-amber-700 border border-amber-200">
              <Building className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-slate-900">
                {isSupplier ? 'Unknown Supplier Review' : 'Unknown Buyer Review'}
              </DialogTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                Match to verified master counterparty or register a new verified entity.
              </p>
            </div>
          </div>
        </DialogHeader>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Counterparty Diagnostic */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3.5 space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">
              Excel Source Ground Truth
            </span>
            <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-slate-200 text-slate-700 font-bold">
              {issue.source_sheet}
            </span>
          </div>
          <div>
            <span className="text-slate-500">Unmatched Value / Code:</span>
            <div className="font-mono font-bold text-slate-900 mt-0.5 text-sm">{issue.legacy_value}</div>
          </div>
          <div>
            <span className="text-slate-500">Diagnostic Details:</span>
            <div className="text-slate-700 mt-0.5">{issue.description}</div>
          </div>
        </div>

        {/* Decision Options */}
        <div className="space-y-3">
          <Label className="text-xs font-bold text-slate-800 uppercase tracking-wider">
            Resolution Action:
          </Label>

          {/* Option 1: Match to Existing */}
          <div
            onClick={() => setDecision('match_existing')}
            className={`p-3 rounded-lg border cursor-pointer transition-all ${
              decision === 'match_existing'
                ? 'border-indigo-600 bg-indigo-50/60 ring-1 ring-indigo-600'
                : 'border-slate-200 hover:border-slate-300 bg-white'
            }`}
          >
            <div className="flex items-start gap-2.5">
              <input
                type="radio"
                name="counterparty_decision"
                checked={decision === 'match_existing'}
                onChange={() => setDecision('match_existing')}
                className="mt-0.5 text-indigo-600 focus:ring-indigo-500"
              />
              <div className="space-y-2 flex-1">
                <div>
                  <span className="text-xs font-bold text-slate-900">
                    Match to Existing Verified {isSupplier ? 'Supplier' : 'Buyer'}
                  </span>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    Maps transactions referencing this code to an existing master record.
                  </p>
                </div>

                {decision === 'match_existing' && (
                  <div className="pt-2 border-t border-indigo-100">
                    <Label className="text-[11px] font-semibold text-slate-700">
                      Select Master {isSupplier ? 'Supplier' : 'Buyer'} *
                    </Label>
                    <select
                      value={matchedId}
                      onChange={(e) => setMatchedId(e.target.value)}
                      className="mt-1 w-full text-xs rounded border border-slate-300 p-2 bg-white text-slate-900"
                    >
                      <option value="">-- Select Master Counterparty --</option>
                      {isSupplier
                        ? suppliers.map((s) => (
                            <option key={s.id} value={s.id}>{s.supplier_name}</option>
                          ))
                        : buyers.map((b) => (
                            <option key={b.id} value={b.id}>{b.buyer_name}</option>
                          ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Option 2: Create New Counterparty */}
          <div
            onClick={() => setDecision('create_new')}
            className={`p-3 rounded-lg border cursor-pointer transition-all ${
              decision === 'create_new'
                ? 'border-indigo-600 bg-indigo-50/60 ring-1 ring-indigo-600'
                : 'border-slate-200 hover:border-slate-300 bg-white'
            }`}
          >
            <div className="flex items-start gap-2.5">
              <input
                type="radio"
                name="counterparty_decision"
                checked={decision === 'create_new'}
                onChange={() => setDecision('create_new')}
                className="mt-0.5 text-indigo-600 focus:ring-indigo-500"
              />
              <div className="space-y-2 flex-1">
                <div>
                  <span className="text-xs font-bold text-slate-900 flex items-center gap-1">
                    <Plus className="w-3.5 h-3.5 text-indigo-600" />
                    Register New {isSupplier ? 'Supplier' : 'Buyer'} Entity
                  </span>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    Creates a new entry in master data with normalized naming.
                  </p>
                </div>

                {decision === 'create_new' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-indigo-100">
                    <div>
                      <Label className="text-[11px] font-semibold text-slate-700">Official Name *</Label>
                      <Input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="e.g. Supplier 0140 Logistics"
                        className="mt-1 text-xs h-8"
                      />
                    </div>
                    {isSupplier && (
                      <div>
                        <Label className="text-[11px] font-semibold text-slate-700">Supplier Category</Label>
                        <select
                          value={newCategory}
                          onChange={(e) => setNewCategory(e.target.value)}
                          className="mt-1 w-full text-xs rounded border border-slate-300 p-2 bg-white text-slate-900"
                        >
                          <option value="feed">Feed Supplier</option>
                          <option value="medicine">Medicine / Vaccines</option>
                          <option value="piglets">Piglet Genetics</option>
                          <option value="hauling">Hauling & Logistics</option>
                          <option value="general">General / Other</option>
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div>
          <Label className="text-xs font-semibold text-slate-700">Audit Notes</Label>
          <Input
            type="text"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="e.g. Verified paper invoice from Supplier 0140"
            className="mt-1 text-xs"
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0 pt-2 border-t border-slate-100">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            className="min-h-[44px] text-xs font-semibold text-slate-700"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || (decision === 'match_existing' && !matchedId)}
            className="min-h-[44px] bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5 text-xs font-semibold"
          >
            {submitting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                <span>Confirm Counterparty Decision</span>
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
